import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import {
  MatDialog,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from '@angular/material/dialog';
import { NoopScrollStrategy } from '@angular/cdk/overlay';
import { Constants } from 'src/app/app.constants';
import { CardService } from '../../../services/index';
import { CardType, StampCard } from '../../../models/index';
import { EmojiDialogComponent } from '../../../features/engagement/emoji-dialog/emoji-dialog.component';

@Component({
  selector: 'configuration-dialog',
  templateUrl: './configuration-dialog.component.html',
  styleUrls: ['./configuration-dialog.component.css'],
})
export class ConfigurationDialogComponent implements OnInit {
  form!: FormGroup;
  // skuFormControl!: FormControl;
  cardFormControl!: FormControl;
  typeFormControl!: FormControl;
  earnMethodFormControl!: FormControl;
  valueFormControl!: FormControl;
  stampsFormControl!: FormControl;
  pointsFormControl!: FormControl;
  pointsCapFormControl!: FormControl;
  pointsRoundingFormControl!: FormControl;
  notificationMessageFormControl!: FormControl;
  notificationMessageWithRewardFormControl!: FormControl;

  loading = false;
  cards: StampCard[] = [];

  cursorPositionNotification = 0;
  cursorPositionNotificationWithReward = 0;

  multipleOrderTypesAllowed = [
    'API',
    'Zapier',
    'Eposnow',
    'Squarespace',
    'Shopify',
    'Square',
  ];

  orderOnlyTypes = ['API', 'Zapier', 'Eposnow', 'Squarespace', 'Shopify'];

  pointsCardsAllowed = ['API', 'Shopify', 'Square'];

  types: any[] = [
    // { name: 'Single Product', value: 'product' },
    { name: 'Order Value', value: 'order' },
  ];

  earningMethods: any[] = [
    {
      name: 'Fixed',
      value: 'fixed',
      info: 'E.g. spend £10 or more to get 10 points or 1 stamp',
      icon: '../../assets/fixed.png',
    },
    {
      name: 'Incremental',
      value: 'incremental',
      info: 'E.g. earn 1 point for every £1 spent',
      icon: '../../assets/incremental.png',
    },
  ];

  pointRoundingOptions: any[] = [
    {
      name: 'Round to nearest whole point',
      value: 'nearest',
      info: 'Example: £10.75 gives 11 points, £10.25 gives 10 points',
    },
    {
      name: 'Round down to whole points',
      value: 'down',
      info: 'Example: £10.75 at 1 point per £1 gives 10 points',
    },
    {
      name: 'Round up to whole points',
      value: 'up',
      info: 'Example: £10.25 at 1 point per £1 gives 11 points',
    },
    {
      name: 'Award exact points',
      value: 'exact',
      info: 'Example: £10.50 at 1 point per £1 gives 10.5 points',
    },
  ];

  editMode = false;

  constructor(
    private cardService: CardService,
    public matDialogRef: MatDialogRef<ConfigurationDialogComponent>,
    public fb: FormBuilder,
    private constants: Constants,
    private dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
    if (this.data?.configuration) {
      if (this.isStrictlyOrderOnlyType(this.data.type)) {
        this.types = [{ name: 'Order Value', value: 'order' }];
      }

      this.editMode = true;
    } else {
      // if (
      //   !this.data.orderAvailable &&
      //   !this.multipleOrderTypesAllowed.includes(this.data.type)
      // ) {
      //   this.types = [{ name: 'Single Product', value: 'product' }];
      // }

      if (this.isStrictlyOrderOnlyType(this.data.type)) {
        this.types = [{ name: 'Order Value', value: 'order' }];
      }
    }

    // this.skuFormControl = new FormControl({
    //   value: this.data.configuration?.sku,
    //   disabled: false,
    // });

    this.valueFormControl = new FormControl({
      value: this.data.configuration?.value,
      disabled: false,
    });

    this.stampsFormControl = new FormControl({
      value: this.data.configuration?.numberOfStamps ?? 1,
      disabled: false,
    });

    this.pointsFormControl = new FormControl({
      value: this.data.configuration?.numberOfPoints ?? 0,
      disabled: false,
    });

    this.cardFormControl = new FormControl({ value: '', disabled: false }, [
      Validators.required,
    ]);

    this.typeFormControl = new FormControl(
      {
        value: this.types[0],
        disabled: true,
      },
      [Validators.required],
    );

    this.pointsCapFormControl = new FormControl({
      value: this.data.configuration?.cap,
      disabled: false,
    });

    this.pointsRoundingFormControl = new FormControl({
      value:
        this.pointRoundingOptions.find(
          (el) => el.value === this.data?.configuration?.pointsRoundingMode,
        ) ?? this.defaultPointsRoundingOption,
      disabled: false,
    });

    this.earnMethodFormControl = new FormControl({
      value: this.earningMethods.find(
        (el) => el.value === this.data?.configuration?.method,
      ),
      disabled: false,
    });

    this.notificationMessageFormControl = new FormControl({
      value:
        this.data.configuration?.notificationMessage ||
        '🎉 You have received {quantity} {type} for your order #{orderNo}',
      disabled: false,
    });

    this.notificationMessageWithRewardFormControl = new FormControl({
      value:
        this.data.configuration?.notificationMessageWithReward ||
        '🎉 You have received {quantity} {type} and {numberOfRewards} {rewardType} for your order #{orderNo}',
      disabled: false,
    });

    if (this.isStrictlyOrderOnlyType(this.data.type)) {
      this.typeFormControl.setValue(this.types[0]);
    }

    this.form = this.fb.group({
      // sku: this.skuFormControl,
      card: this.cardFormControl,
      type: this.typeFormControl,
      method: this.earnMethodFormControl,
      cap: this.pointsCapFormControl,
      pointsRoundingMode: this.pointsRoundingFormControl,
      value: this.valueFormControl,
      'Number of stamps': this.stampsFormControl,
      'Number of points': this.pointsFormControl,
      notificationMessage: this.notificationMessageFormControl,
      notificationMessageWithReward:
        this.notificationMessageWithRewardFormControl,
    });
  }

  ngOnInit(): void {
    this.getCards();
  }

  isStrictlyOrderOnlyType(type: string): boolean {
    return (
      this.orderOnlyTypes.includes(type) &&
      !this.multipleOrderTypesAllowed.includes(type)
    );
  }

  getCards(): void {
    this.loading = true;
    this.cardService
      .getCards(100, 0)
      .subscribe({
        next: (res: any) => {
          if (res) {
            this.cards = res.docs.filter(
              (el: StampCard) => el.cardType !== CardType.point,
            );

            if (!this.pointsCardsAllowed.includes(this.data.type)) {
              this.cards = this.cards.filter(
                (el) => el.cardType == CardType.stamp,
              );
            }

            this.cards.forEach((card) => {
              card.info = card.description;
            });

            this.cardFormControl.setValue(
              this.cards.find(
                (el) => el._id?.toString() === this.data?.configuration?.card,
              ),
            );
          }
        },
        error: (res: HttpErrorResponse) => {
          this.constants.snack(res.error.message);
        },
      })
      .add(() => (this.loading = false));
  }

  save(): void {
    if (this.form.invalid) {
      const invalidFields = Object.keys(this.form.controls).filter(
        (field) => this.form.controls[field].invalid,
      );

      this.constants.snack(
        'Please complete all fields: ' + invalidFields.join(', '),
      );
      return;
    }

    if (this.cardFormControl?.value?.cardType === 'stamp') {
      if (this.stampsFormControl.value < 1) {
        this.constants.snack('Number of stamps cannot be less than 1');
        return;
      }

      if (
        this.stampsFormControl.value >
        this.cardFormControl.value?.numberOfStamps
      ) {
        this.constants.snack(
          'Number of stamps cannot be more than available stamps for this card',
        );
        return;
      }
    } else {
      if (this.pointsFormControl.value < 1) {
        this.constants.snack('Number of points cannot be less than 1');
        return;
      }

      if (this.cardFormControl?.value?.cardType === 'point') {
        if (
          this.pointsFormControl.value >
          this.cardFormControl.value?.points?.totalPoints
        ) {
          this.constants.snack(
            'Number of points cannot be more than available points for this card',
          );
          return;
        }
      }
    }

    let data = {
      type: this.typeFormControl.value.value,
      card: this.cardFormControl.value._id,
      sku: null,
      value: null,
      _id: null,
      numberOfStamps: null,
      numberOfPoints: null,
      method: null,
      cap: null,
      pointsRoundingMode: null,
      notificationMessage: null,
      notificationMessageWithReward: null,
    };

    if (this.cardFormControl?.value?.cardType === 'stamp') {
      data['numberOfStamps'] = this.stampsFormControl.value ?? 1;
    } else {
      data['numberOfPoints'] = this.pointsFormControl.value ?? 1;

      data['method'] = this.earnMethodFormControl.value.value;
      data['cap'] = this.pointsCapFormControl.value;
      data['pointsRoundingMode'] = this.showPointsRoundingField
        ? (this.pointsRoundingFormControl.value?.value ??
          this.defaultPointsRoundingOption.value)
        : null;
    }

    if (this.editMode) {
      data['_id'] = this.data.configuration._id;
    }

    if (data.type === 'product') {
      // data['sku'] = this.skuFormControl.value?.trim();
    } else {
      data['value'] = this.valueFormControl.value;
    }

    if (this.notificationMessageFormControl.value?.trim()) {
      data['notificationMessage'] =
        this.notificationMessageFormControl.value.trim();
    }
    if (this.notificationMessageWithRewardFormControl.value?.trim()) {
      data['notificationMessageWithReward'] =
        this.notificationMessageWithRewardFormControl.value.trim();
    }

    this.matDialogRef.close(data);
  }

  get infoBody(): string {
    return `Use this form to set up how customers earn stamps or points for purchases from your <b>${this.data.type}</b> store.`;
  }

  get skuTooltip(): string {
    if (this.data.type === 'Shopify') {
      return `You can find this in your Shopify Product's overview page`;
    } else {
      return `You can find this in your Square Item's overview page`;
    }
  }

  typeChanged(): void {
    if (this.typeFormControl.value?.value === 'product') {
      // this.skuFormControl.setValidators([Validators.required]);
      this.valueFormControl.clearValidators();
      this.earnMethodFormControl.clearValidators();
    } else {
      this.valueFormControl.setValidators([Validators.required]);
      // this.skuFormControl.clearValidators();
      this.earnMethodFormControl.setValidators([Validators.required]);
    }

    // this.skuFormControl.updateValueAndValidity();
    this.valueFormControl.updateValueAndValidity();

    this.pointsFormControl.reset();
    this.stampsFormControl.reset();
    this.earnMethodFormControl.setValue(null);
    this.pointsRoundingFormControl.setValue(this.defaultPointsRoundingOption);
    this.valueFormControl.reset();
    this.cardFormControl.setValue(null);
  }

  earningMethodChanged(): void {
    // if (this.typeFormControl.value?.value === 'product') {
    //   this.skuFormControl.setValidators([Validators.required]);
    //   this.valueFormControl.clearValidators();
    // } else {
    //   this.valueFormControl.setValidators([Validators.required]);
    //   this.skuFormControl.clearValidators();
    // }
    // this.skuFormControl.updateValueAndValidity();
    // this.valueFormControl.updateValueAndValidity();
  }

  get stampNumbers() {
    return Array.from(
      { length: this.cardFormControl.value.numberOfStamps },
      (_, i) => i + 1,
    );
  }

  cardSelected(): void {
    this.stampsFormControl.reset();
    this.pointsFormControl.reset();
    this.earnMethodFormControl.setValue(null);
    this.pointsRoundingFormControl.setValue(this.defaultPointsRoundingOption);
    this.valueFormControl.reset();

    if (this.cardFormControl.value.cardType === CardType.stamp) {
      this.stampsFormControl.setValidators([Validators.required]);
      this.pointsFormControl.clearValidators();
      this.earningMethods[0].info = `E.g. spend £10 or more to get 1 stamp`;
      this.earningMethods[1].info = `E.g. earn 1 stamp for every £10 spent`;
    } else {
      this.pointsFormControl.setValidators([Validators.required]);
      this.stampsFormControl.clearValidators();
      this.earningMethods[0].info = `E.g. spend £10 or more to get 10 points`;
      this.earningMethods[1].info = `E.g. earn 10 points for every £10 spent`;

      if (!this.editMode) {
        const card = this.cardFormControl.value;
        const earningMethod = card.points.earningMethod;
        const value = card.points.value;
        const points = card.points.pointsPerScan;
        const max = card.points.maxPoints;

        if (earningMethod === 'incremental') {
          this.earnMethodFormControl.setValue(this.earningMethods[1]);
        } else if (earningMethod === 'fixed') {
          this.earnMethodFormControl.setValue(this.earningMethods[0]);
        }

        if (value) {
          this.valueFormControl.setValue(value);
        }

        if (max) {
          this.pointsCapFormControl.setValue(max);
        }

        if (points) {
          this.pointsFormControl.setValue(points);
        }
      }
    }
  }

  get stampOrPoints(): string {
    return this.cardFormControl.value.cardType === CardType.stamp
      ? 'stamp'
      : 'point';
  }

  get minimumSpendInfoText(): string {
    return `The amount the customer must spend in order to qualify for a the specified number of ${this.stampOrPoints}s`;
  }

  get incrementalInfoText(): string {
    return `The number of ${this.stampOrPoints}s the customer should earn for every £x spent`;
  }

  get maximumNumberOfPointsCapInfoText(): string {
    return `The maximum number of points a customer can earn for a single purchase`;
  }

  get showNumberOfStampsField(): boolean {
    return (
      this.cardFormControl.value &&
      this.cardFormControl.value?.cardType === 'stamp'
    );
  }

  get showNumberOfPointsField(): boolean {
    return (
      this.cardFormControl.value &&
      this.cardFormControl.value?.cardType !== 'stamp' &&
      (this.typeFormControl.value?.value !== 'order' ||
        this.earnMethodFormControl.value?.value === 'fixed')
    );
  }

  get incrementalPointsSummary(): string {
    return `Customers will earn ${this.pointsFormControl.value} ${
      this.pointsFormControl.value === 1 ? 'point' : 'points'
    } for every £${this.valueFormControl.value} spent`;
  }

  get defaultPointsRoundingOption(): any {
    return this.pointRoundingOptions[0];
  }

  get showPointsRoundingField(): boolean {
    return (
      this.showIncrementalField &&
      this.cardFormControl.value?.cardType !== CardType.stamp
    );
  }

  get showMinimumSpendField(): boolean {
    return (
      this.typeFormControl.value?.value === 'order' &&
      this.cardFormControl.value &&
      (this.earnMethodFormControl.value?.value === 'fixed' ||
        this.cardFormControl.value?.cardType === 'stamp')
    );
  }

  get showIncrementalField(): boolean {
    return (
      this.typeFormControl.value?.value === 'order' &&
      this.cardFormControl.value &&
      this.earnMethodFormControl.value?.value === 'incremental'
    );
  }

  get showIncrementalSummaryField(): boolean {
    return (
      this.showIncrementalField &&
      this.valueFormControl.value &&
      this.pointsFormControl.value
    );
  }

  get notificationPreview(): string {
    return this.formatNotificationPreview(
      this.notificationMessageFormControl.value,
    );
  }

  get notificationWithRewardPreview(): string {
    return this.formatNotificationPreview(
      this.notificationMessageWithRewardFormControl.value,
      true,
    );
  }

  private formatNotificationPreview(
    template: string,
    includeReward = false,
  ): string {
    let preview = template || '';

    const quantity = this.previewQuantity;
    const replacements: { [key: string]: string } = {
      '{quantity}': quantity.toString(),
      '{type}': this.previewAwardType(quantity),
      '{orderNo}': '12345',
      '{orderValue}': '£25.00',
    };

    if (includeReward) {
      replacements['{numberOfRewards}'] = '1';
      replacements['{rewardType}'] = 'reward';
    }

    Object.keys(replacements).forEach((key) => {
      preview = preview.replace(
        new RegExp(this.escapeRegExp(key), 'g'),
        replacements[key],
      );
    });

    return preview;
  }

  private get previewQuantity(): number {
    if (this.cardFormControl.value?.cardType === CardType.stamp) {
      return Number(this.stampsFormControl.value) || 5;
    }

    return Number(this.pointsFormControl.value) || 5;
  }

  private previewAwardType(quantity: number): string {
    const singular =
      this.cardFormControl.value?.cardType === CardType.stamp
        ? 'stamp'
        : 'point';

    return quantity === 1 ? singular : `${singular}s`;
  }

  private escapeRegExp(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  updateCursorPositionNotification(event: any): void {
    this.cursorPositionNotification = event.target.selectionStart;
  }

  updateCursorPositionNotificationWithReward(event: any): void {
    this.cursorPositionNotificationWithReward = event.target.selectionStart;
  }

  insertVariableIntoNotification(variable: string): void {
    const currentValue = this.notificationMessageFormControl.value || '';
    const newValue =
      currentValue.slice(0, this.cursorPositionNotification) +
      variable +
      currentValue.slice(this.cursorPositionNotification);
    this.notificationMessageFormControl.setValue(newValue);
    this.cursorPositionNotification += variable.length;
  }

  insertVariableIntoNotificationWithReward(variable: string): void {
    const currentValue =
      this.notificationMessageWithRewardFormControl.value || '';
    const newValue =
      currentValue.slice(0, this.cursorPositionNotificationWithReward) +
      variable +
      currentValue.slice(this.cursorPositionNotificationWithReward);
    this.notificationMessageWithRewardFormControl.setValue(newValue);
    this.cursorPositionNotificationWithReward += variable.length;
  }

  openEmojiPickerForNotification(): void {
    const dialogRef = this.dialog.open(EmojiDialogComponent, {
      autoFocus: false,
      scrollStrategy: new NoopScrollStrategy(),
    });

    dialogRef.afterClosed().subscribe((data) => {
      if (data?.emoji) {
        this.insertEmojiIntoNotification(data.emoji);
      }
    });
  }

  insertEmojiIntoNotification(emoji: string): void {
    const currentValue = this.notificationMessageFormControl.value || '';
    const newValue =
      currentValue.slice(0, this.cursorPositionNotification) +
      emoji +
      currentValue.slice(this.cursorPositionNotification);
    this.notificationMessageFormControl.setValue(newValue);
    this.cursorPositionNotification += emoji.length;
  }

  openEmojiPickerForNotificationWithReward(): void {
    const dialogRef = this.dialog.open(EmojiDialogComponent, {
      autoFocus: false,
      scrollStrategy: new NoopScrollStrategy(),
    });

    dialogRef.afterClosed().subscribe((data) => {
      if (data?.emoji) {
        this.insertEmojiIntoNotificationWithReward(data.emoji);
      }
    });
  }

  insertEmojiIntoNotificationWithReward(emoji: string): void {
    const currentValue =
      this.notificationMessageWithRewardFormControl.value || '';
    const newValue =
      currentValue.slice(0, this.cursorPositionNotificationWithReward) +
      emoji +
      currentValue.slice(this.cursorPositionNotificationWithReward);
    this.notificationMessageWithRewardFormControl.setValue(newValue);
    this.cursorPositionNotificationWithReward += emoji.length;
  }

  get showEarningMethodField(): boolean {
    return (
      this.typeFormControl.value?.value === 'order' &&
      this.cardFormControl.value &&
      this.cardFormControl?.value?.cardType !== 'stamp'
    );
  }

  get showSkuField(): boolean {
    return (
      this.typeFormControl.value?.value === 'product' &&
      this.cardFormControl.value
    );
  }
}
